import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';

export const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low', 'Info'];
export const SEVERITY_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3, Info: 4 };
export const SEVERITY_SCORE = { Critical: 5, High: 4, Medium: 3, Low: 2, Info: 1 };
export const SEVERITY_COLORS = {
  Critical: '#6a1b9a',
  High: '#c62828',
  Medium: '#ef6c00',
  Low: '#1565c0',
  Info: '#2e7d32',
};

export const METHODOLOGY_PARAGRAPHS = [
  'We begin each external assessment by defining the authorized scope and building an accurate picture of the internet-facing attack surface. The assessment team reviews the provided targets, correlates them with passive intelligence, and performs reconnaissance to identify exposed domains, subdomains, hosts, and supporting infrastructure that may expand the reachable perimeter.',
  'After the initial mapping phase, we validate exposure through controlled active enumeration. We perform subdomain discovery, DNS resolution, port scanning, and service identification to determine which assets are reachable and which entry points warrant deeper review. From there, we fingerprint the observable technology stack, including web servers, frameworks, middleware, TLS configurations, and other implementation details that shape the likely attack paths.',
  'Once the environment is understood, we align the discovered technologies and services with relevant weaknesses, misconfigurations, and known CVEs. The assessment team uses that context to guide targeted exploitation, prioritizing the issues most likely to produce real impact. Testing is performed deliberately and with evidence-driven judgment so that findings reflect validated risk rather than theoretical exposure.',
  'Throughout the engagement, we document affected assets, record supporting evidence, and capture the technical details required to reproduce each issue. The assessment team then translates those results into a clear report that explains what was identified, how it was validated, and which remediation actions should be prioritized first. The goal is to provide both leadership and technical teams with a practical path from reconnaissance through remediation.',
];

const gradeMessages = {
  'A+': 'Outstanding security posture. No material risk identified.',
  'A-': 'Strong posture with minor opportunities for hardening.',
  B: 'Good overall, address medium and low issues to further improve resilience.',
  C: 'Fair posture. Multiple findings should be prioritized for remediation.',
  D: 'Weak posture. Significant vulnerabilities require attention.',
  F: 'Critical risk. Immediate remediation is recommended.',
};

const markdownProcessor = unified().use(remarkParse).use(remarkGfm);

const ipv4Re = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
const cidrRe = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}\/([0-9]|[12]\d|3[0-2])$/;
const domainRe = /^(?=.{1,253}$)(([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63})$/i;

const genericFindingLabels = new Set([
  'name',
  'title',
  'finding',
  'finding name',
  'description',
  'details',
  'location',
  'remediation',
  'remediation steps',
  'recommendation',
  'recommendations',
  'impact',
  'severity',
  'evidence',
  'proof',
  'references',
  'endpoint',
  'endpoints',
  'asset',
  'assets',
  'host',
  'hosts',
  'summary',
]);

export const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

export const hexToRgb = (hex) => {
  const normalized = String(hex || '').replace('#', '').trim();
  const full = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;
  const int = Number.parseInt(full || '000000', 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

export const mixHex = (baseHex, targetHex, weight = 0.5) => {
  const base = hexToRgb(baseHex);
  const target = hexToRgb(targetHex);
  const ratio = Math.max(0, Math.min(1, weight));
  const mix = (from, to) => Math.round(from + ((to - from) * ratio));

  return `#${[mix(base.r, target.r), mix(base.g, target.g), mix(base.b, target.b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
};

export const alpha = (hex, opacity) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const buildBrand = (primaryColor = '#0A68FF') => ({
  primary: primaryColor,
  primaryDark: mixHex(primaryColor, '#000000', 0.35),
  primaryMid: mixHex(primaryColor, '#0f172a', 0.15),
  primaryLight: mixHex(primaryColor, '#ffffff', 0.28),
  ink: '#0f172a',
});

export const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return new Date().toLocaleDateString();

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

export const normalizeSeverity = (severity) => {
  if (!severity) return 'Medium';
  const normalized = String(severity).trim().toLowerCase();
  if (normalized.startsWith('crit')) return 'Critical';
  if (normalized.startsWith('high')) return 'High';
  if (normalized.startsWith('med')) return 'Medium';
  if (normalized.startsWith('low')) return 'Low';
  if (normalized.startsWith('info') || normalized.startsWith('inform')) return 'Info';
  return 'Medium';
};

export const extractSeverity = (value) => {
  if (!value) return 'Medium';

  if (typeof value === 'object') {
    if (value.severity) return normalizeSeverity(value.severity);
    if (value.report_details) return extractSeverity(value.report_details);
    return 'Medium';
  }

  if (typeof value === 'string') {
    const match = value.match(/(?:^|\n)#{1,3}\s*Severity[\s:]*\n?\s*(Critical|High|Medium|Low|Info)\b/i);
    if (match?.[1]) return normalizeSeverity(match[1]);
  }

  return 'Medium';
};

export const cleanReportDetails = (value = '') =>
  String(value).replace(/##\s*Severity[\s\S]*?\n{2,}/i, '').trim();

export const stripMarkdownText = (value = '') =>
  String(value)
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[(.*?)\]\(.*?\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*[-+*]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();

const isPlaceholderFindingTitle = (value = '') => {
  const cleaned = stripMarkdownText(value).toLowerCase().replace(/:$/, '');
  return !cleaned || genericFindingLabels.has(cleaned) || ['n/a', 'na', 'none', 'unknown'].includes(cleaned);
};

const extractNamedSectionValue = (value = '') => {
  const match = String(value).match(
    /(?:^|\n)#{1,6}\s*(?:Name|Title|Finding Name)\s*\n+([\s\S]*?)(?=\n#{1,6}\s+|$)/i
  );

  if (!match?.[1]) return '';

  return match[1]
    .split(/\r?\n/)
    .map(stripMarkdownText)
    .find((line) => line && !isPlaceholderFindingTitle(line)) || '';
};

export const compactMeta = (value = '', maxLength = 56) => {
  const cleaned = stripMarkdownText(value);
  if (!cleaned) return '';
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 3).trim()}...` : cleaned;
};

export const extractTitle = (details = '', fallback = '') => {
  const cleanFallback = stripMarkdownText(fallback);
  if (cleanFallback && !isPlaceholderFindingTitle(cleanFallback)) return cleanFallback;

  const namedSection = extractNamedSectionValue(details);
  if (namedSection) return namedSection;

  const headingMatches = Array.from(String(details).matchAll(/^\s*#{1,6}\s+(.+)$/gm));
  for (const match of headingMatches) {
    const heading = stripMarkdownText(match[1]);
    if (heading && !isPlaceholderFindingTitle(heading)) return heading;
  }

  const firstMeaningfulLine = String(details)
    .split(/\r?\n/)
    .map(stripMarkdownText)
    .find((line) => line && !isPlaceholderFindingTitle(line));

  return firstMeaningfulLine || cleanFallback || 'Finding';
};

export const getGrade = (score) => {
  if (score == null) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

export const getGradeColor = (grade) => {
  switch (grade) {
    case 'A+':
    case 'A-':
      return '#2e7d32';
    case 'B':
      return '#1565c0';
    case 'C':
      return '#ef6c00';
    case 'D':
      return '#fbc02d';
    case 'F':
      return '#c62828';
    default:
      return '#0A68FF';
  }
};

export const classifyAsset = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return { type: 'unknown', value: trimmed, raw: value };
  if (cidrRe.test(trimmed)) return { type: 'cidr', value: trimmed, raw: value };
  if (ipv4Re.test(trimmed)) return { type: 'ip', value: trimmed, raw: value };

  const noProtocol = trimmed.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
  if (noProtocol.startsWith('*.')) {
    const base = noProtocol.replace(/^\*\./, '');
    if (domainRe.test(base)) return { type: 'domain', value: base, raw: value, wasWildcard: true };
  }

  if (domainRe.test(noProtocol)) return { type: 'domain', value: noProtocol, raw: value };
  return { type: 'unknown', value: trimmed, raw: value };
};

export const getAssetTypeLabel = (type) => {
  switch (type) {
    case 'domain':
      return 'Domain';
    case 'ip':
      return 'IP';
    case 'cidr':
      return 'CIDR';
    default:
      return 'Other';
  }
};

export const getAssetTypeColor = (type, brand) => {
  switch (type) {
    case 'domain':
      return brand.primaryMid;
    case 'ip':
      return '#7c4dff';
    case 'cidr':
      return '#00bfa5';
    default:
      return '#94a3b8';
  }
};

export const flattenInlineText = (tokens = []) =>
  tokens.map((token) => {
    if (!token) return '';
    if (token.type === 'text' || token.type === 'inlineCode') return token.value || '';
    if (token.type === 'break') return ' ';
    return flattenInlineText(token.children || []);
  }).join('');

const convertInlineNodes = (nodes = []) =>
  nodes.flatMap((node) => {
    if (!node) return [];

    switch (node.type) {
      case 'text':
        return node.value ? [{ type: 'text', value: node.value }] : [];
      case 'strong':
        return [{ type: 'strong', children: convertInlineNodes(node.children) }];
      case 'emphasis':
        return [{ type: 'emphasis', children: convertInlineNodes(node.children) }];
      case 'delete':
        return [{ type: 'delete', children: convertInlineNodes(node.children) }];
      case 'inlineCode':
        return [{ type: 'inlineCode', value: node.value || '' }];
      case 'link':
        return [{ type: 'link', url: node.url, children: convertInlineNodes(node.children) }];
      case 'break':
        return [{ type: 'break' }];
      case 'image':
        return node.alt ? [{ type: 'text', value: node.alt }] : [];
      default:
        return node.children ? convertInlineNodes(node.children) : [];
    }
  });

export const extractBlocksText = (blocks = []) =>
  blocks.map((block) => {
    if (!block) return '';
    if (block.type === 'paragraph' || block.type === 'heading') return flattenInlineText(block.inlines);
    if (block.type === 'code') return block.value || '';
    if (block.type === 'list') return block.items.map((item) => extractBlocksText(item.blocks)).join(' ');
    if (block.type === 'blockquote') return extractBlocksText(block.blocks);
    if (block.type === 'table') return block.rows.map((row) => row.map(flattenInlineText).join(' ')).join(' ');
    return '';
  }).join(' ').replace(/\s+/g, ' ').trim();

const convertMdastNodes = (nodes = []) =>
  nodes.flatMap((node) => {
    if (!node) return [];

    switch (node.type) {
      case 'heading':
        return [{
          type: 'heading',
          depth: node.depth,
          inlines: convertInlineNodes(node.children),
        }];
      case 'paragraph':
        return [{
          type: 'paragraph',
          inlines: convertInlineNodes(node.children),
        }];
      case 'list':
        return [{
          type: 'list',
          ordered: Boolean(node.ordered),
          start: node.start || 1,
          items: node.children.map((item) => ({
            checked: item.checked,
            blocks: convertMdastNodes(item.children),
          })),
        }];
      case 'blockquote':
        return [{
          type: 'blockquote',
          blocks: convertMdastNodes(node.children),
        }];
      case 'code':
        return [{
          type: 'code',
          value: node.value || '',
          lang: node.lang || '',
        }];
      case 'table':
        return [{
          type: 'table',
          rows: node.children.map((row) =>
            row.children.map((cell) => {
              const contentBlocks = convertMdastNodes(cell.children);
              if (contentBlocks.length === 1 && contentBlocks[0].type === 'paragraph') {
                return contentBlocks[0].inlines;
              }
              return [{ type: 'text', value: extractBlocksText(contentBlocks) }];
            })
          ),
        }];
      case 'thematicBreak':
        return [{ type: 'divider' }];
      default:
        return node.children ? convertMdastNodes(node.children) : [];
    }
  });

export const markdownToBlocks = (markdown = '') => {
  if (!String(markdown).trim()) return [];
  const parsed = markdownProcessor.parse(String(markdown));
  const tree = markdownProcessor.runSync(parsed);
  return convertMdastNodes(tree.children || []);
};

export const buildExternalReportPdfData = ({
  pentestId,
  primaryColor,
  companyName,
  clientName,
  stats,
  exploits,
  executiveSummary,
  scopeRaw,
}) => {
  const brand = buildBrand(primaryColor || '#0A68FF');
  const cleanedExploits = (exploits || [])
    .filter((item) => item && (item.name || item.report_details))
    .map((item, index) => {
      const cleanedDetails = cleanReportDetails(item.report_details || '');
      const severity = extractSeverity(item);
      const title = extractTitle(cleanedDetails || item.report_details || '', item.name || `Finding ${index + 1}`);

      return {
        ...item,
        severity,
        cleanedDetails,
        title,
        asset: compactMeta(item.impacted_assets || item.endpoint || item.host || item.asset || ''),
        id: `${slugify(title || item.name || `finding-${index + 1}`)}-${index + 1}`,
        markdownBlocks: markdownToBlocks(cleanedDetails || 'No additional details provided.'),
      };
    })
    .sort((left, right) => (SEVERITY_RANK[left.severity] ?? 999) - (SEVERITY_RANK[right.severity] ?? 999));

  const counts = stats?.counts || {};
  const totalFindings = counts?.exploits ?? counts?.findings ?? cleanedExploits.length;
  const openPorts = counts?.open_ports ?? 0;
  const totalAssets = counts?.assets ?? counts?.hosts ?? 0;
  const riskScore = stats?.risk_score ?? stats?.score ?? null;
  const grade = getGrade(riskScore);
  const gradeColor = getGradeColor(grade);

  const severityCounts = SEVERITY_ORDER.reduce((accumulator, label) => ({
    ...accumulator,
    [label]: 0,
  }), {});

  cleanedExploits.forEach((item) => {
    severityCounts[item.severity] = (severityCounts[item.severity] || 0) + 1;
  });

  const countedFindings = SEVERITY_ORDER.reduce((sum, label) => sum + (severityCounts[label] || 0), 0);
  const visualFindingsTotal = Math.max(totalFindings, countedFindings);
  const highImpactCount = (severityCounts.Critical || 0) + (severityCounts.High || 0);
  const highImpactShare = visualFindingsTotal ? Math.round((highImpactCount / visualFindingsTotal) * 100) : 0;
  const maxSeverityCount = Math.max(1, ...SEVERITY_ORDER.map((label) => severityCounts[label] || 0));

  const severityStats = SEVERITY_ORDER.map((label) => {
    const value = severityCounts[label] || 0;
    return {
      label,
      value,
      color: SEVERITY_COLORS[label],
      share: visualFindingsTotal ? Math.round((value / visualFindingsTotal) * 100) : 0,
    };
  });

  const topFindings = [...cleanedExploits]
    .map((item, index) => ({
      id: item.id || `finding-${index + 1}`,
      title: item.title,
      severity: item.severity,
      asset: item.asset,
    }))
    .sort((left, right) => (SEVERITY_SCORE[right.severity] || 0) - (SEVERITY_SCORE[left.severity] || 0))
    .slice(0, 5);

  const summaryParagraphs = String(executiveSummary || '')
    .split(/\n{2,}/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const scopeLines = String(scopeRaw || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const scopeItems = scopeLines.map(classifyAsset);
  const scopeTotals = scopeItems.reduce((accumulator, item) => ({
    ...accumulator,
    [item.type]: (accumulator[item.type] || 0) + 1,
  }), { domain: 0, ip: 0, cidr: 0, unknown: 0 });
  const scopePreview = scopeItems.slice(0, 10);

  const tocItems = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-security-assessment', title: 'Executive Security Assessment', level: 1 },
    { id: 'scope-excerpt', title: 'Scope (Excerpt)', level: 1 },
    { id: 'top-findings', title: 'Top Findings', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    { id: 'findings', title: 'Findings', level: 1 },
    ...cleanedExploits.map((item) => ({
      id: item.id,
      title: item.title,
      level: 2,
    })),
  ];

  return {
    pentestId,
    clientName: clientName || stats?.meta?.client_name || stats?.client || 'Client Name',
    companyName: companyName || 'StealthNet AI',
    reportDate: formatDate(stats?.meta?.generated_at || Date.now()),
    testingType: stats?.meta?.type || 'External Network',
    organizationName: stats?.meta?.client_name || stats?.client || clientName || 'Client',
    brand,
    grade,
    gradeColor,
    gradeMessage: gradeMessages[grade] || '',
    riskScore,
    totalFindings: visualFindingsTotal,
    openPorts,
    totalAssets,
    severityStats,
    maxSeverityCount,
    highImpactCount,
    highImpactShare,
    topFindings,
    executiveSummary: summaryParagraphs,
    methodology: METHODOLOGY_PARAGRAPHS,
    scopeItems: scopePreview,
    scopeTotals,
    findings: cleanedExploits,
    tocItems,
  };
};
